import React from "react";
import { AppCard, AppCardContent, AppCardHeader, AppCardTitle } from "../../ui";
import SectionMark from "./SectionMark";
import { needsSections, paperSummary } from "./builderRules";

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** What one section serves, in a few words. */
export const sectionLine = (s) => [
  s.questionCount ? plural(s.questionCount, s.random ? "random question" : "question") : "",
  s.codingCount ? plural(s.codingCount, "coding problem") : "",
].filter(Boolean).join(" + ") || "Empty";

export const minutesText = (totals) => {
  if (!totals.minutes) return "No time limit";
  return `${totals.minutes} min${totals.timedSections ? ", timed per section" : ""}`;
};

export const marksText = (totals) => (totals.marks == null ? "Depends on the draw" : plural(totals.marks, "mark"));

/**
 * PaperSummary — the assessment at a glance, beside the step being edited:
 * how many questions, marks and minutes, and what each section holds.
 */
export default function PaperSummary({ form }) {
  const { sections, totals } = paperSummary(form);
  const sectioned = needsSections(form) && sections.length > 1;
  const facts = [
    ["Questions", totals.coding ? `${totals.questions} + ${plural(totals.coding, "coding problem")}` : String(totals.questions)],
    ["Marks", marksText(totals)],
    ["Time", minutesText(totals)],
  ];

  return (
    <AppCard as="aside" aria-label="Assessment summary">
      <AppCardHeader>
        <AppCardTitle as="h2">At a glance</AppCardTitle>
      </AppCardHeader>
      <AppCardContent className="tw-flex tw-flex-col tw-gap-4">
        <dl className="tw-m-0 tw-grid tw-grid-cols-[auto_minmax(0,1fr)] tw-gap-x-4 tw-gap-y-1.5 tw-text-[0.8125rem]">
          {facts.map(([k, v]) => (
            <React.Fragment key={k}>
              <dt className="tw-font-normal tw-text-content-muted">{k}</dt>
              <dd className="tw-m-0 tw-text-right tw-font-medium tw-tabular-nums tw-text-content">{v}</dd>
            </React.Fragment>
          ))}
        </dl>
        {sectioned && (
          <ol className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-2.5 tw-border-0 tw-border-t tw-border-solid tw-border-line tw-p-0 tw-pt-4">
            {sections.map((s) => (
              <li key={s.key} className="tw-flex tw-items-start tw-gap-2.5">
                <SectionMark letter={s.letter} size="sm" />
                <span className="tw-flex tw-min-w-0 tw-flex-col">
                  <span className="tw-truncate tw-text-[0.8125rem] tw-font-medium tw-text-content">{s.title}</span>
                  <span className={s.empty ? "tw-text-xs tw-text-warning-fg" : "tw-text-xs tw-text-content-subtle"}>
                    {sectionLine(s)}{s.minutes ? ` · ${s.minutes} min` : ""}
                  </span>
                </span>
              </li>
            ))}
          </ol>
        )}
      </AppCardContent>
    </AppCard>
  );
}
